<template>
  <section class="my-8 mx-4 md:mx-28 bg-black p-6 md:p-10">
    <div class="mb-8">
      <h1 class="text-[22px] text-gray-400 border-y-2 py-4 text-left font-bold">
        Terms of Use
      </h1>
      <p class="text-gray-500 text-xs mt-3">Last updated: 11 August 2026</p>
    </div>

    <div class="flex flex-col text-gray-400 gap-6 text-sm text-left">
      <p>
        These terms are the agreement between you and
        <span class="text-[#FF0000]">Rally Navigator, LLC</span> covering this
        website, the Rally Navigator desktop application, and the mobile
        roadbook reader. Together we call these the Software. By downloading,
        installing or using any of them, you agree to these terms. If you do not
        agree, do not use the Software. Questions about these terms go to
        <a class="text-[#FF0000]" href="mailto:mike@rallynavigator.com"
          >mike@rallynavigator.com</a
        >.
      </p>

      <div>
        <h2 class="text-gray-300 font-bold underline mb-2">
          Your Licence to Use the Software
        </h2>
        <p class="mb-2">
          We grant you a personal, limited, non-exclusive, non-transferable,
          revocable licence to install and use the Software for creating rally
          roadbooks, for as long as you comply with these terms and, where a
          paid plan applies, keep that subscription current.
        </p>
        <p>
          This is a licence, not a sale. We and our licensors keep ownership of
          the Software. You get the right to use it, and nothing else is
          granted to you by implication or otherwise.
        </p>
      </div>

      <div>
        <h2 class="text-gray-300 font-bold underline mb-2">
          Free, Premium and Pro
        </h2>
        <p class="mb-2">
          The Free and Premium versions are both for personal, non-commercial
          use. Roadbooks they produce carry a "Not for Commercial Use" marking,
          and that marking is part of the licence terms, not a cosmetic detail.
        </p>
        <p>
          A Pro Rally subscription grants a commercial licence and removes that
          marking. If you produce roadbooks for a paid event, for a client, or
          for any other commercial purpose, you need a current Pro subscription
          for the entire period in which you do so. Removing, obscuring, editing
          or otherwise defeating the marking without a Pro licence is a breach
          of these terms and an infringement of our copyright.
        </p>
      </div>

      <div>
        <h2 class="text-gray-300 font-bold underline mb-2">
          Ownership of the Software
        </h2>
        <p class="mb-2">
          The Software, this website, and everything in them are owned by Rally
          Navigator, LLC or licensed to it, and are protected by copyright,
          trademark and other laws.
          That includes the source code and object code, the interface design
          and layout, the navigation icon libraries and all icon artwork, the
          tulip diagram artwork, the written text and documentation on this
          site, the screenshots, the sample roadbook and GPX, KMZ and KML files
          we publish, and the compilation and arrangement of all of it.
        </p>
        <p>
          "Rally Navigator" and the Rally Navigator logo are our trademarks. You
          may not use them, or anything confusingly similar to them, as the name
          of or branding for your own product or service, or in a way that
          suggests we produced, sponsored or endorsed something we did not.
        </p>
      </div>

      <div>
        <h2 class="text-gray-300 font-bold underline mb-2">
          What You May Not Do
        </h2>
        <p class="mb-2">You may not, and may not permit anyone else to:</p>
        <p class="mb-2">
          <strong>Copy or redistribute.</strong> Copy, publish, distribute,
          sell, rent, lease, sublicense, lend or share the Software or any part
          of it, except for the backup copies you need for your own use.
        </p>
        <p class="mb-2">
          <strong>Reverse engineer.</strong> Reverse engineer, decompile,
          disassemble, unpack, extract, decrypt or otherwise attempt to derive
          the source code, file formats, algorithms or internal structure of the
          Software, except to the narrow extent that applicable law expressly
          permits despite this restriction.
        </p>
        <p class="mb-2">
          <strong>Build derivative or competing works from ours.</strong>
          Create derivative works of the Software, or use the Software, its
          code, its artwork, its interface, its documentation or its output as
          the source, reference or training material for developing any product
          or service that competes with Rally Navigator.
        </p>
        <p class="mb-2">
          <strong>Circumvent licensing.</strong> Bypass, disable, tamper with or
          work around any licence check, activation, subscription verification,
          usage limit or technical protection measure, or use the Software with
          a licence key you did not obtain from us.
        </p>
        <p class="mb-2">
          <strong>Scrape or harvest.</strong> Use any robot, spider, crawler,
          scraper, headless browser or other automated means to access, copy,
          index, monitor or collect content from this website or the Software,
          or mirror or frame any part of this website, other than the ordinary
          operation of a public search engine obeying our robots.txt.
        </p>
        <p class="mb-2">
          <strong>Strip notices.</strong> Remove, obscure or alter any
          copyright, trademark, watermark, attribution or other proprietary
          notice in the Software or its output.
        </p>
        <p>
          <strong>Interfere.</strong> Probe or test the security of our systems,
          overload or disrupt our servers or network, or access any account,
          system or data you have not been given permission to access.
        </p>
      </div>

      <div>
        <h2 class="text-gray-300 font-bold underline mb-2">
          Your Roadbooks and Your Content
        </h2>
        <p>
          The roadbooks, routes, tracks, notes and files you create with the
          Software are yours. We claim no ownership of them, and we do not
          licence them from you. You are responsible for having the rights to
          any map data, imagery, logos, photographs or other material you bring
          into a roadbook, and for how you use and distribute the result.
        </p>
      </div>

      <div>
        <h2 class="text-gray-300 font-bold underline mb-2">
          Maps and Third-Party Data
        </h2>
        <p>
          The Software can display map and imagery layers supplied by third
          parties, including Google, Mapbox, ESRI, USGS, IGN France, IGN Spain,
          OpenStreetMap, OpenTopoMap and CyclOSM. Those layers are owned by
          those providers and supplied under their own terms and licences,
          including their rules on attribution, caching, printing and commercial
          use. Your use of them is between you and them, and it is your
          responsibility to comply. Availability and coverage can change or be
          withdrawn by a provider at any time, and we cannot guarantee any layer
          will keep working.
        </p>
      </div>

      <div>
        <h2 class="text-gray-300 font-bold underline mb-2">
          Subscriptions and Payment
        </h2>
        <p class="mb-2">
          Paid plans are billed in advance for the term shown at checkout and
          renew automatically at the then-current price unless you cancel before
          the renewal date. You can cancel at any time; cancelling stops future
          billing and your access continues to the end of the term you have
          already paid for.
        </p>
        <p>
          Payments are handled by our payment processors, currently Stripe and
          PayPal, under their own terms. We may change our prices, and any
          change takes effect at your next renewal, not during a term you have
          already paid for. If a payment fails or is reversed, we may suspend
          the paid features until it is resolved.
        </p>
      </div>

      <div>
        <h2 class="text-gray-300 font-bold underline mb-2">
          Safety and Navigation
        </h2>
        <p class="mb-2">
          Rally Navigator is a roadbook authoring tool. It is not a certified
          navigation, survey or safety system, and it is not a substitute for
          your own judgement, reconnaissance and verification.
        </p>
        <p>
          Distances, headings, elevations, GPS coordinates, tulip diagrams and
          map imagery can be inaccurate, out of date or wrong. Before anyone
          relies on a roadbook you produce, you are responsible for checking it
          against the ground and against the rules of the governing body for
          your event. Do not use the Software while driving or riding. You are
          responsible for the safety of your event, your route and the people
          following it.
        </p>
      </div>

      <div>
        <h2 class="text-gray-300 font-bold underline mb-2">
          No Warranty
        </h2>
        <p>
          The Software is provided "as is" and "as available", without
          warranties of any kind, whether express, implied or statutory,
          including any implied warranty of merchantability, fitness for a
          particular purpose, accuracy, or non-infringement. We do not warrant
          that the Software will be uninterrupted, error-free, or that any
          defect will be corrected. Some jurisdictions do not allow the
          exclusion of certain warranties, so parts of this section may not
          apply to you.
        </p>
      </div>

      <div>
        <h2 class="text-gray-300 font-bold underline mb-2">
          Limitation of Liability
        </h2>
        <p>
          To the fullest extent permitted by law, Rally Navigator, LLC is not
          liable for any indirect, incidental, special, consequential,
          exemplary or punitive damages, or for lost profits, lost data, lost
          goodwill, or business interruption, arising out of or relating to the
          Software, however caused and on any theory of liability. Our total
          liability for all claims relating to the Software is limited to the
          amount you paid us in the twelve months before the event giving rise
          to the claim. Some jurisdictions do not allow these limitations, so
          parts of this section may not apply to you.
        </p>
      </div>

      <div>
        <h2 class="text-gray-300 font-bold underline mb-2">Indemnity</h2>
        <p>
          You agree to indemnify and hold harmless Rally Navigator, LLC against
          any claim, loss, liability, cost or expense, including reasonable
          legal fees, arising from your use of the Software, your breach of
          these terms, your infringement of anyone else's rights, or any
          roadbook or event you produce or run using the Software.
        </p>
      </div>

      <div>
        <h2 class="text-gray-300 font-bold underline mb-2">Termination</h2>
        <p>
          We may suspend or terminate your licence and your account if you
          breach these terms. On termination, your right to use the Software
          ends and you must stop using it and remove your installed copies. The
          sections on ownership, restrictions, warranty, liability, indemnity
          and governing law survive termination. Nothing in this section limits
          any other remedy available to us.
        </p>
      </div>

      <div>
        <h2 class="text-gray-300 font-bold underline mb-2">
          Reporting Infringement
        </h2>
        <p>
          If you believe material on this site infringes your copyright, email
          <a class="text-[#FF0000]" href="mailto:mike@rallynavigator.com"
            >mike@rallynavigator.com</a
          >
          with the work you say has been infringed, where the material is on our
          site, your contact details, and a statement that you believe in good
          faith that the use is not authorised and that your notice is accurate.
          We investigate every notice we receive.
        </p>
      </div>

      <div>
        <h2 class="text-gray-300 font-bold underline mb-2">
          Governing Law
        </h2>
        <p>
          These terms are governed by the laws of the State of Nevada, United
          States, without regard to its conflict of laws rules, and the state
          and federal courts located in Washoe County, Nevada have exclusive
          jurisdiction over any dispute arising out of them. You consent to the
          personal jurisdiction of those courts. If any
          part of these terms is found unenforceable, the rest stays in force.
          Our not enforcing a term on one occasion is not a waiver of it.
        </p>
      </div>

      <div>
        <h2 class="text-gray-300 font-bold underline mb-2">
          Changes to These Terms
        </h2>
        <p>
          If we change these terms we will post the updated version here and
          change the date at the top of this page. Continuing to use the
          Software after a change means you accept the updated terms.
        </p>
      </div>

      <div>
        <h2 class="text-gray-300 font-bold underline mb-2">Privacy</h2>
        <p>
          How we handle your information is covered separately in our
          <router-link class="text-[#FF0000]" to="/privacy-policy"
            >Privacy Policy</router-link
          >, which forms part of these terms.
        </p>
      </div>
    </div>
  </section>
  <PartnersSmall />
  <RallyLogoDownload />
</template>

<script>
import PartnersSmall from './PartnersSmall.vue';
import RallyLogoDownload from './RallyLogoDownload.vue';

export default {
  name: 'TermsOfUse',
  components: {
    PartnersSmall,
    RallyLogoDownload,
  },
};
</script>

<style scoped></style>
